import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { ordersService } from '../services/orders.service';
import {
  AdminOrderListItem,
  OrderStatus,
  VendorWithOrders,
  UserWithOrders,
} from '../types/orders.types';
import { useMessageHandler } from './useMessageHandler';
import { useStateRef } from './useStateRef';

interface UseOrdersParams {
  section?: 'all' | 'disputed' | 'refunds';
  status?: OrderStatus;
  vendorId?: string;
  userId?: string;
  search?: string;
  limit?: number;
  useNewPagination?: boolean; // Feature flag for new cursor pagination
}

export const useOrders = (params: UseOrdersParams = {}) => {
  const paramsRef = useRef(params);
  const showMessage = useMessageHandler();
  
  const [orders, , setOrders] = useStateRef<AdminOrderListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [vendors, setVendors] = useState<VendorWithOrders[]>([]);
  const [users, setUsers] = useState<UserWithOrders[]>([]);
  const [vendorsLoading, setVendorsLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);

  const cursorRef = useRef<string | undefined>(undefined);
  const isFirstLoadRef = useRef(true);
  const prevStatusRef = useRef<OrderStatus | undefined>(params.status);
  const prevSectionRef = useRef<string | undefined>(params.section);
  const prevVendorIdRef = useRef<string | undefined>(params.vendorId);
  const prevUserIdRef = useRef<string | undefined>(params.userId);
  const prevSearchRef = useRef<string | undefined>(params.search);
  const currentStatusRef = useRef<OrderStatus | undefined>(params.status);
  const currentSectionRef = useRef<string | undefined>(params.section);

  // Update current refs every render
  currentStatusRef.current = params.status;
  currentSectionRef.current = params.section;

  // Effect to reset when filters change (INCLUDING search now - it's backend-handled)
  useEffect(() => {
    const statusChanged = prevStatusRef.current !== params.status;
    const sectionChanged = prevSectionRef.current !== params.section;
    const filtersChanged = 
      prevVendorIdRef.current !== params.vendorId ||
      prevUserIdRef.current !== params.userId ||
      prevSearchRef.current !== params.search; // Search now triggers reset
    
    if (statusChanged || sectionChanged || filtersChanged) {
      console.log('[useOrders] Reset detected', {
        statusChanged,
        sectionChanged,
        filtersChanged,
        oldCursor: cursorRef.current ? 'present' : 'none',
      });
      
      // Clear everything
      cursorRef.current = undefined;
      isFirstLoadRef.current = true;
      prevStatusRef.current = params.status;
      prevSectionRef.current = params.section;
      prevVendorIdRef.current = params.vendorId;
      prevUserIdRef.current = params.userId;
      prevSearchRef.current = params.search; // Update search ref
      
      // Clear orders array
      setOrders([]);
      setCurrentPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.status, params.section, params.vendorId, params.userId, params.search]); // search added back

  const pageSize = params.limit || 10;
  
  // Backend handles ALL filtering including search
  // Frontend just displays what backend returns, sorted newest first
  const filteredOrders = useMemo(
    () => [...orders].sort((a, b) => {
      const dateA = typeof a.createdAt === 'number' ? a.createdAt : new Date(a.createdAt || 0).getTime();
      const dateB = typeof b.createdAt === 'number' ? b.createdAt : new Date(b.createdAt || 0).getTime();
      return dateB - dateA;
    }),
    [orders]
  );
  
  const displayStartIndex = (currentPage - 1) * pageSize;
  const displayOrders = filteredOrders.slice(displayStartIndex, displayStartIndex + pageSize);
  const isDisplayFullSize = displayOrders.length === pageSize;
  const hasMore = !!cursorRef.current;
  const needsMoreData = !isDisplayFullSize && hasMore;

  // Use new pagination if status is provided and useNewPagination is true (default)
  // BUT: Fall back to legacy pagination if filters (vendorId, userId, search) are applied
  // because the new GSI-based pagination doesn't support filtering
  // ALSO: When status is undefined (All section), use the special 'all' endpoint if no filters
  const hasFilters = !!(params.vendorId || params.userId || params.search);
  const shouldUseAllEndpoint = !params.status && !hasFilters && params.section === 'all';
  const useNewPagination = params.useNewPagination !== false && (!!params.status || shouldUseAllEndpoint) && !hasFilters;

  /**
   * Fetch more orders from backend with cursor (NEW - optimized)
   */
  const fetchMoreWithNewPagination = useCallback(
    async () => {
      // CRITICAL: Check if status/section changed since this function was queued
      // This prevents using stale cursor from previous section
      if (currentStatusRef.current !== params.status || currentSectionRef.current !== params.section) {
        console.log('[useOrders] Fetch aborted - status/section changed', {
          expectedStatus: params.status,
          currentStatus: currentStatusRef.current,
          expectedSection: params.section,
          currentSection: currentSectionRef.current,
        });
        return;
      }

      // Read cursor directly from ref to get latest value
      const cursor = cursorRef.current;
      
      // Determine which status to use: if undefined, use 'all' for the special endpoint
      const statusForEndpoint = params.status || 'all';

      // Log for debugging
      console.log('[useOrders] fetchMoreWithNewPagination called', {
        status: statusForEndpoint,
        cursor: cursor ? 'present' : 'none',
      });

      setLoading(true);

      try {
        // Convert status to string for the API call
        const statusString = typeof statusForEndpoint === 'string' ? statusForEndpoint : statusForEndpoint;
        
        const response = await ordersService.getOrdersByStatus(
          statusString,
          cursor,
          pageSize
        );

        // Double-check status/section hasn't changed while we were fetching
        if (currentStatusRef.current !== params.status || currentSectionRef.current !== params.section) {
          console.log('[useOrders] Fetch result discarded - status/section changed during fetch');
          return;
        }

        cursorRef.current = response.afterCursor;
        setOrders(prev => [...prev, ...response.items]);
      } catch (err: any) {
        const errorMessage = err.response?.data?.message || 'Failed to fetch orders';
        showMessage({ error: errorMessage });
      } finally {
        setLoading(false);
      }
    },
    [params.status, params.section, pageSize, setOrders, showMessage]
  );

  /**
   * Fetch more orders from backend with cursor (LEGACY)
   * Search is now handled by backend
   */
  const fetchMoreWithLegacyPagination = useCallback(
    async () => {
      // CRITICAL: Check if status/section changed since this function was queued
      if (currentStatusRef.current !== params.status || currentSectionRef.current !== params.section) {
        console.log('[useOrders] Legacy fetch aborted - status/section changed');
        return;
      }

      // Read cursor directly from ref to get latest value
      const cursor = cursorRef.current;
      
      setLoading(true);

      try {
        // Backend handles all filtering including search
        const response = await ordersService.getAllOrders({
          limit: pageSize,
          afterCursor: cursor,
          status: params.status,
          vendorId: params.vendorId,
          userId: params.userId,
          search: params.search, // Backend search
          section: params.section || 'all',
        });

        // Double-check status/section hasn't changed while we were fetching
        if (currentStatusRef.current !== params.status || currentSectionRef.current !== params.section) {
          console.log('[useOrders] Legacy fetch result discarded - status/section changed during fetch');
          return;
        }

        if (response.success) {
          cursorRef.current = response.pagination.endCursor;
          setOrders(prev => [...prev, ...response.orders]);
        } else {
          showMessage({ error: 'Failed to fetch orders' });
        }
      } catch (err: any) {
        const errorMessage = err.response?.data?.message || 'Failed to fetch orders';
        showMessage({ error: errorMessage });
      } finally {
        setLoading(false);
      }
    },
    [params.status, params.section, params.vendorId, params.userId, params.search, pageSize, setOrders, showMessage]
  );

  // Choose the appropriate fetch function
  const fetchMore = useNewPagination ? fetchMoreWithNewPagination : fetchMoreWithLegacyPagination;

  // Auto-fetch when needs more data OR when filters change (INCLUDING search)
  useEffect(() => {
    console.log('[useOrders] Auto-fetch effect triggered', {
      isFirstLoad: isFirstLoadRef.current,
      needsMoreData,
      cursor: cursorRef.current ? 'present' : 'none',
      status: params.status,
      section: params.section,
    });
    
    if (isFirstLoadRef.current || needsMoreData) {
      isFirstLoadRef.current = false;
      fetchMore();
    }
  }, [needsMoreData, fetchMore, params.status, params.section, params.vendorId, params.userId, params.search]); // search added back

  // Reset when filters change (legacy) - INCLUDING search
  useEffect(() => {
    if (!useNewPagination) {
      paramsRef.current = params;
      setOrders([]);
      setCurrentPage(1);
      cursorRef.current = undefined;
      isFirstLoadRef.current = true;
    }
  }, [params.section, params.vendorId, params.userId, params.search, setOrders, useNewPagination]); // search added back

  /**
   * Update order status
   */
  const updateOrderStatus = useCallback(
    async (orderId: string, newStatus: OrderStatus) => {
      try {
        const response = await ordersService.updateOrderStatus(orderId, { status: newStatus });

        if (response.success) {
          setOrders((prev) =>
            prev.map((order) =>
              order.orderId === orderId ? { ...order, status: newStatus } : order
            )
          );
          showMessage({ message: 'Order status updated successfully' });
          return true;
        } else {
          showMessage({ error: 'Failed to update order status' });
          return false;
        }
      } catch (err: any) {
        const errorMessage = err.response?.data?.message || 'Failed to update order status';
        showMessage({ error: errorMessage });
        return false;
      }
    },
    [setOrders, showMessage]
  );

  /**
   * Fetch vendors with orders (only once)
   */
  const fetchVendors = useCallback(async () => {
    try {
      setVendorsLoading(true);
      const response = await ordersService.getVendorsWithOrders();
      if (response.success) {
        setVendors(response.vendors);
      }
    } catch (err: any) {
      console.error('Failed to fetch vendors:', err);
    } finally {
      setVendorsLoading(false);
    }
  }, []);

  /**
   * Fetch users with orders (only once)
   */
  const fetchUsers = useCallback(async () => {
    try {
      setUsersLoading(true);
      const response = await ordersService.getUsersWithOrders();
      if (response.success) {
        setUsers(response.users);
      }
    } catch (err: any) {
      console.error('Failed to fetch users:', err);
    } finally {
      setUsersLoading(false);
    }
  }, []);

  // Fetch vendors and users on mount (only once)
  useEffect(() => {
    fetchVendors();
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pagination info (EXACT catalogue pattern)
  // Use filteredOrders for accurate counts when multiple filters are applied
  const curItemsCount = filteredOrders.length;
  const curPagesCount = Math.ceil(curItemsCount / pageSize);
  const curPageStartIndex = displayStartIndex + 1;
  const curPageEndIndex = displayStartIndex + displayOrders.length;

  return {
    orders: displayOrders,
    allOrders: filteredOrders, // Return filtered orders, not raw orders
    loading: loading && displayOrders.length === 0,
    currentPage,
    setCurrentPage,
    hasMore,
    totalPages: curPagesCount,
    totalItems: curItemsCount,
    pageStartIndex: curPageStartIndex,
    pageEndIndex: curPageEndIndex,
    updateOrderStatus,
    vendors,
    users,
    vendorsLoading,
    usersLoading,
  };
};
